"use client";

import { useId, useRef, useState } from "react";
import Link from "next/link";
import { identifyPosthogByEmail } from "@/lib/posthog-identify-email";

interface BlackFridayEarlyAccessLabels {
	placeholder: string;
	cta: string;
	consent: string;
	privacyLink: string;
	success: string;
	error: string;
	invalidEmail: string;
	consentRequired: string;
}

interface BlackFridayEarlyAccessFormProps {
	locale: string;
	channel: string;
	privacyHref: string;
	labels: BlackFridayEarlyAccessLabels;
}

/**
 * Freiwillige Anmeldung für den frühen Zugang. Nutzt denselben Endpunkt wie der
 * Newsletter, aber einen eigenen `source`, damit die Auswertung Kampagnen- und
 * Newsletter-Anmeldungen trennen kann. Kein `name` an den Feldern und
 * `action="#"`, damit eine nicht hydratisierte Seite die E-Mail nicht in die
 * URL schreibt.
 */
export function BlackFridayEarlyAccessForm({
	locale,
	channel,
	privacyHref,
	labels,
}: BlackFridayEarlyAccessFormProps) {
	const inputId = useId();
	const feedbackId = `${inputId}-feedback`;
	const dedupeRef = useRef<string | null>(null);
	const [email, setEmail] = useState("");
	const [consent, setConsent] = useState(false);
	const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
	const [error, setError] = useState<string | null>(null);

	async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setError(null);

		const trimmed = email.trim();
		if (!trimmed) {
			setError(labels.invalidEmail);
			return;
		}
		if (!consent) {
			setError(labels.consentRequired);
			return;
		}

		setStatus("loading");
		try {
			const response = await fetch("/api/newsletter", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email: trimmed,
					consent,
					locale,
					channel,
					source: "black-friday-early-access",
				}),
			});
			const data = (await response.json().catch(() => null)) as { error?: string } | null;

			if (!response.ok) {
				setStatus("error");
				setError(
					data?.error === "invalid_email" || data?.error === "email_required"
						? labels.invalidEmail
						: labels.error,
				);
				return;
			}

			identifyPosthogByEmail(
				trimmed,
				{ newsletter_signup: true, newsletter_source: "black-friday-early-access" },
				dedupeRef,
			);
			setEmail("");
			setConsent(false);
			setStatus("success");
		} catch {
			setStatus("error");
			setError(labels.error);
		}
	}

	if (status === "success") {
		return (
			<p role="status" className="text-sm font-medium text-foreground">
				{labels.success}
			</p>
		);
	}

	return (
		<form action="#" method="get" onSubmit={handleSubmit} className="w-full space-y-3">
			<div className="flex flex-col gap-2 sm:flex-row">
				<label htmlFor={inputId} className="sr-only">
					{labels.placeholder}
				</label>
				<input
					id={inputId}
					type="email"
					autoComplete="email"
					inputMode="email"
					value={email}
					onChange={(event) => setEmail(event.target.value)}
					placeholder={labels.placeholder}
					disabled={status === "loading"}
					aria-invalid={Boolean(error)}
					aria-describedby={error ? feedbackId : undefined}
					className="h-11 w-full min-w-0 rounded-md border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60 sm:flex-[1_1_14rem]"
				/>
				<button
					type="submit"
					disabled={status === "loading"}
					className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:opacity-90 disabled:opacity-60"
				>
					{labels.cta}
				</button>
			</div>

			<label className="flex cursor-pointer items-start gap-3 text-left">
				<input
					type="checkbox"
					checked={consent}
					onChange={(event) => setConsent(event.target.checked)}
					disabled={status === "loading"}
					className="mt-0.5 h-4 w-4 shrink-0 rounded border border-border bg-background"
				/>
				<span className="text-xs leading-relaxed text-muted-foreground">
					{labels.consent}{" "}
					<Link
						href={privacyHref}
						className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
					>
						{labels.privacyLink}
					</Link>
				</span>
			</label>

			{error && (
				<p id={feedbackId} role="alert" className="text-xs text-destructive">
					{error}
				</p>
			)}
		</form>
	);
}
